/**
 * "Shape at a glance" — figure S4's six mini tiles in the left panel:
 * Projects · Holes · Bridges · Open ground · Synergy cells · Left off the map
 * (the last spanning both columns). Every tile carries a one-line help
 * sentence so no label is left unexplained (UX-023).
 *
 * A tile prints a NUMBER only when something actually computed it. A measured
 * zero is the digit 0; a value nobody has computed prints the reason instead.
 * "--" is never shown, and a 0 never stands in for "not computed" — that is the
 * exact dishonesty the dashboard rules ban.
 */
import type { OwnScopeShapeTile } from '../../lib/whitespace'

export interface OwnShapeGlanceProps {
  tiles: OwnScopeShapeTile[]
  /** Labels of tiles whose numbers are still being fetched. Such a tile reads
   * "Computing…" instead of "Not computed yet", which looks like missing data
   * (UX-058). Once the request settles without a number, the tile goes back to
   * the honest "Not computed yet". */
  computing?: ReadonlySet<string>
}

export default function OwnShapeGlance({ tiles, computing }: OwnShapeGlanceProps) {
  return (
    <section data-part="OwnShapeGlance" aria-label="Shape at a glance" className="mt-4">
      <h3 className="mb-1.5 text-[12.5px] font-bold text-gray-900">Shape at a glance</h3>
      <dl className="grid grid-cols-2 gap-2">
        {tiles.map((tile, i) => (
          <div
            key={tile.label}
            className={`rounded-lg border border-gray-200 bg-white px-2.5 py-2 ${i === tiles.length - 1 ? 'col-span-2' : ''}`}
          >
            <dt className="text-[12px] font-bold uppercase leading-tight tracking-wide text-gray-600">
              {tile.label}
            </dt>
            <dd className="mt-0.5 text-[19px] font-extrabold leading-none text-gray-900">
              {tile.value === null ? (
                computing?.has(tile.label) ? (
                  <span role="status" aria-busy="true" className="animate-pulse text-[12px] font-normal text-gray-600">
                    Computing…
                  </span>
                ) : (
                  <span className="text-[12px] font-normal text-gray-600">{tile.note}</span>
                )
              ) : (
                tile.value
              )}
            </dd>
            {tile.value !== null && tile.note && (
              <p className="mt-0.5 text-[12px] text-gray-600">{tile.note}</p>
            )}
            <p data-testid="shape-tile-help" className="mt-0.5 text-[12px] leading-snug text-gray-600">
              {tile.help}
            </p>
          </div>
        ))}
      </dl>
    </section>
  )
}
